// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, inject, ChangeDetectionStrategy, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { CompanyService, DatadogFeatureTrackingService } from '@lfx/core/services';
import { KeyProjectsService } from '../services/key-projects.service';
import { KeyProjectsCsvExportService } from '../services/key-projects-csv-export.service';
import { PageHeaderComponent } from '@lfx/key-projects/components/page-header/page-header.component';
import { KeyProjectsTableComponent } from '@lfx/key-projects/components/key-projects-table/key-projects-table.component';
import { ButtonComponent } from '@lfx/shared/components/button';
import { EmptyStateComponent } from '@lfx/key-projects/components/empty-state/empty-state.component';
import { LfBoxComponent } from '@lfx/shared/components/lf-box/lf-box.component';
import { AddWorkspaceModalComponent } from '@lfx/key-projects/components/add-workspace-modal/add-workspace-modal.component';
import { AddProjectsModalComponent } from '@lfx/key-projects/components/add-projects-modal/add-projects-modal.component';
import { RenameWorkspaceModalComponent } from '@lfx/key-projects/components/rename-workspace-modal/rename-workspace-modal.component';
import { ModalService } from '@lfx/shared/services/modal.service';
import { ToastService } from '@lfx/core/services';
import { keyProjectModalOptions } from '@lfx/config/modal-config';
import { take } from 'rxjs/operators';
import { WORKSPACE_NAMES } from '@lfx/key-projects/key-projects.constants';

@Component({
  selector: 'lfx-key-projects',
  imports: [
    CommonModule,
    PageHeaderComponent,
    KeyProjectsTableComponent,
    ButtonComponent,
    EmptyStateComponent,
    LfBoxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="key-projects-page">
      <!-- Page Header - Always visible -->
      <lfx-page-header [title]="pageTitle" [description]="pageDescription">
        <lfx-button
          icon="far fa-folder-plus"
          variant="tertiary"
          size="large"
          [disabled]="initialLoading()"
          (click)="addWorkspace()"
        >
          Add workspace
        </lfx-button>
      </lfx-page-header>

      <!-- Initial Error State -->
      @if (initialError() && !initialLoading()) {
        <div class="initial-error-container">
          <i class="fal fa-exclamation-triangle"></i>
          <span>{{ initialError() }}</span>
          <lfx-button variant="secondary" size="small" (click)="retryInitialLoad()">
            Try Again
          </lfx-button>
        </div>
      }

      <!-- Normal Content - Show empty state or table -->
      @if (!initialError()) {
        <!-- Empty State - Show for loading, no workspaces, or workspace mode -->
        @if (shouldShowEmptyState()) {
          <lfx-lf-box>
            <lfx-empty-state
              [config]="keyProjectsService.emptyStateConfig()"
              (actionClicked)="onEmptyStateAction()"
            >
            </lfx-empty-state>
          </lfx-lf-box>
        }

        <!-- Key Projects Table or Loading Spinner -->
        @if (!shouldShowEmptyState()) {
          @if (loadingWorkspaceProjects()) {
            <lfx-lf-box
              sectionTitle="Workspace Projects"
              subtitle="Loading project data and metrics from your selected workspace. This may take a few moments..."
              [isLoading]="true"
            >
            </lfx-lf-box>
          } @else {
            <lfx-key-projects-table
              [keyProjects]="keyProjectsService.currentProjects()"
              [isLoading]="isLoading"
              [viewState]="keyProjectsService.viewState()"
              [emptyStateConfig]="getTableEmptyStateConfig()"
              [workspaces]="keyProjectsService.workspaces()"
              (exportRequested)="exportToCsv()"
              (workspaceSelected)="showWorkspaceProjects($event)"
              (emptyStateActionClicked)="onEmptyStateAction()"
              (addWorkspaceClicked)="addWorkspace()"
              (addProjectClicked)="addProject()"
              (removeProjectFromWorkspaceClicked)="removeProjectFromWorkspace($event)"
              (deleteWorkspaceClicked)="deleteWorkspace($event)"
              (renameWorkspaceClicked)="renameWorkspace()"
              (rebuildAllWorkspacesClicked)="rebuildAllWorkspaces()"
            >
            </lfx-key-projects-table>
          }
        }
      }
    </div>
  `,
  styles: [`
    .initial-error-container {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: var(--lf-spacing-16);
      padding: var(--lf-spacing-48);
      text-align: center;
    }

    .initial-error-container i {
      color: var(--lf-color-danger);
      font-size: var(--lf-font-size-lg);
    }

  `]
})
export class KeyProjectsComponent implements OnDestroy {
  protected readonly keyProjectsService = inject(KeyProjectsService);
  private readonly csvExportService = inject(KeyProjectsCsvExportService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly companyService = inject(CompanyService);
  private readonly featureTracking = inject(DatadogFeatureTrackingService);

  // Component state
  protected readonly isLoading = false;

  // Flag to track if workspace auto-selection has been handled
  private workspaceAutoSelectHandled = false;

  // Capture navigation state immediately (before it's lost)
  private readonly navigationState = history.state;

  // Initial loading state from service
  protected readonly initialLoading = this.keyProjectsService.initialLoading;
  protected readonly initialError = this.keyProjectsService.initialError;
  protected readonly creatingDefaultWorkspaces = this.keyProjectsService.creatingDefaultWorkspaces;
  protected readonly loadingWorkspaceProjects = this.keyProjectsService.loadingWorkspaceProjects;

  // Page content - always static
  protected readonly pageTitle = 'Key projects';

  protected readonly pageDescription =
    "Identify our company's key open source projects, understand their health status, and assess our company's influence.";

  constructor() {
    // Set feature context for Datadog tracking
    this.featureTracking.setFeatureContext({
      feature: 'key-projects',
      page: 'workspace-list',
    });

    // Handle navigation state for workspace auto-selection
    // Use effect to watch for workspaces to be loaded
    effect(() => {
      const workspaces = this.keyProjectsService.workspaces();
      const initialLoading = this.initialLoading();

      // Only auto-select once, after workspaces are loaded
      if (!initialLoading && workspaces.length > 0 && !this.workspaceAutoSelectHandled) {
        this.handleNavigationState();
      }
    });
  }

  /**
   * Handle navigation state from Home page to auto-select workspace
   */
  private handleNavigationState(): void {
    if (this.navigationState?.action === 'selectAllProjectsWorkspace') {
      this.workspaceAutoSelectHandled = true;
      this.selectAllProjectsWorkspace();
    }
  }

  /**
   * Select the "All Projects with Activities" workspace
   */
  private selectAllProjectsWorkspace(): void {
    const workspaces = this.keyProjectsService.workspaces();
    const allProjectsWorkspace = workspaces.find(
      ws => ws.name === WORKSPACE_NAMES.ALL_PROJECTS
    );

    if (allProjectsWorkspace) {
      this.showWorkspaceProjects(allProjectsWorkspace.id);
    }
  }

  protected addWorkspace(): void {
    this.featureTracking.trackModalInteraction('key-projects', 'add-workspace', 'open');

    this.modalService
      .openComponent(AddWorkspaceModalComponent).pipe(take(1)).subscribe(workspace => {
        if (workspace) {
          this.featureTracking.trackCrudOperation('key-projects', 'create', 'workspace', 'success', {
            workspaceName: workspace.name,
          });
          this.toastService.showSuccess(`Workspace "${workspace.name}" has been created successfully.`, {
            header: 'Workspace Created'
          });
        } else {
          this.featureTracking.trackModalInteraction('key-projects', 'add-workspace', 'cancel');
        }
      });
  }

  protected renameWorkspace(): void {
    this.modalService
      .openComponent(RenameWorkspaceModalComponent, { options: keyProjectModalOptions })
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          console.log('Workspace renamed successfully:', result);
          this.toastService.showSuccess(`Workspace has been renamed to "${result.name}".`, {
            header: 'Workspace Renamed'
          });
        }
      });
  }

  protected addProject(): void {
    this.modalService.openComponent(AddProjectsModalComponent, { options: keyProjectModalOptions }).pipe(take(1)).subscribe({
      next: (result) => {
        if (result?.success) {
          console.log('Projects added successfully:', result);
          const projectCount = result.projectCount || 1;
          const message = projectCount === 1
            ? 'Project has been added successfully.'
            : `${projectCount} projects have been added successfully.`;
          this.toastService.showSuccess(message, {
            header: 'Projects Added'
          });
        }
      },
      error: (error) => {
        console.error('Error in add project modal:', error);
        this.toastService.showError('Failed to add projects. Please try again.', {
          header: 'Add Projects Failed'
        });
      }
    });
  }

  protected deleteWorkspace(event: { id: string; name: string }): void {
    // Check if workspace has projects
    const workspace = this.keyProjectsService.getWorkspaceById(event.id);
    const projectCount = workspace?.projectSlugs?.length || 0;

    let confirmationMessage: string;
    if (projectCount > 0) {
      confirmationMessage = `"${event.name}" contains ${projectCount} project${projectCount === 1 ? '' : 's'}. All projects will be removed from this workspace. Are you sure you want to delete it? This action cannot be undone.`;
    } else {
      confirmationMessage = `Are you sure you want to delete "${event.name}"? This action cannot be undone.`;
    }

    this.modalService
      .confirmDanger('Delete Workspace', confirmationMessage)
      .subscribe(confirmed => {
        if (confirmed) {
          console.log('Deleting workspace:', event);
          this.keyProjectsService.deleteWorkspace(event.id)
            .subscribe({
              next: () => {
                console.log(`Successfully deleted workspace "${event.name}"`);
                this.toastService.showSuccess(`Workspace "${event.name}" has been deleted successfully.`, {
                  header: 'Workspace Deleted'
                });
                // Note: Optimistic updates and navigation handled by service
              },
              error: (error) => {
                console.error('Error deleting workspace:', error);
                this.toastService.showError(`Failed to delete workspace "${event.name}". Please try again.`, {
                  header: 'Delete Failed'
                });

                // Show user-friendly error message
                if (error.message?.includes('409') || error.message?.includes('conflict')) {
                  // Offer to remove projects and try again
                  this.modalService
                    .confirmDanger(
                      'Remove Projects and Delete?',
                      `"${event.name}" cannot be deleted because it contains projects. Would you like to remove all projects from this workspace and then delete it?`
                    )
                    .subscribe(confirmed => {
                      if (confirmed) {
                        console.log('Force deleting workspace with projects removal:', event);
                        this.keyProjectsService.deleteWorkspace(event.id, true)
                          .subscribe({
                            next: () => {
                              console.log(`Successfully force deleted workspace "${event.name}"`);
                              this.toastService.showSuccess(`Workspace "${event.name}" and all its projects have been deleted successfully.`, {
                                header: 'Workspace Deleted'
                              });
                            },
                            error: (forceError) => {
                              console.error('Error force deleting workspace:', forceError);
                              this.toastService.showError(`Failed to delete "${event.name}" even after removing projects. Please contact support.`, {
                                header: 'Delete Failed'
                              });
                              this.modalService.info(
                                'Delete Failed',
                                `Failed to delete "${event.name}" even after removing projects. Please contact support.`
                              ).subscribe();
                            }
                          });
                      }
                    });
                } else {
                  this.modalService.info(
                    'Delete Failed',
                    `Failed to delete "${event.name}". Please try again or contact support if the problem persists.`
                  ).subscribe();
                }
              }
            });
        }
      });
  }

  /**
   * Export current projects to CSV
   * Delegates all business logic to KeyProjectsCsvExportService
   * Controller only handles validation and user feedback
   */
  protected exportToCsv(): void {
    const viewState = this.keyProjectsService.viewState();
    const projects = this.keyProjectsService.currentProjects();

    // Validation - Check if there's data to export
    if (!projects || projects.length === 0) {
      this.toastService.showInfo('No data to export');
      return;
    }

    // Delegate export logic to service
    const exportedCount = this.csvExportService.exportToCsv(projects, viewState);

    // Track export action
    this.featureTracking.trackExport('key-projects', 'csv', exportedCount, {
      workspaceId: viewState.selectedWorkspaceId,
    });

    // User feedback
    this.toastService.showSuccess(`Exported ${exportedCount} projects to CSV`);
  }

  protected showWorkspaceProjects(workspaceId: string): void {
    this.keyProjectsService.showWorkspaceProjects(workspaceId);
    this.setDatadogWorkspaceContext(workspaceId);
  }

  private setDatadogWorkspaceContext(workspaceId: string): void {
    const workspace = this.keyProjectsService.getWorkspaceById(workspaceId);
    if (workspace && datadogRum.getInitConfiguration()) {
      datadogRum.setGlobalContextProperty('keyProject', {
        workspaceId: workspace.id,
        workspaceName: workspace.name,
        page: 'list'
      });
    }
  }

  protected onEmptyStateAction(): void {
    if (this.keyProjectsService.workspaces().length === 0) {
      this.addWorkspace();
    } else {
      this.addProject();
    }
  }

  protected shouldShowEmptyState(): boolean {
    // Show empty state during initial loading
    if (this.initialLoading()) {
      return true;
    }

    // Do NOT show empty state if workspace projects are loading
    if (this.loadingWorkspaceProjects()) {
      return false;
    }

    // Show empty state if no workspaces exist (initial user journey)
    if (this.keyProjectsService.workspaces().length === 0) {
      return true;
    }

    return false;
  }

  protected getTableEmptyStateConfig() {
    const viewState = this.keyProjectsService.viewState();
    if (viewState.mode === 'workspace-projects') {
      return this.keyProjectsService.emptyStateConfig();
    }
    return null;
  }

  protected removeProjectFromWorkspace(event: {
    projectId: string;
    projectName: string;
    workspaceId: string;
  }): void {
    this.modalService
      .confirmDanger(
        'Remove Project',
        `Are you sure you want to remove "${event.projectName}" from this workspace?`
      )
      .subscribe(confirmed => {
        if (confirmed) {
          console.log('Removing project from workspace:', event);
          this.keyProjectsService.removeProjectFromWorkspace(event.workspaceId, event.projectId)
            .subscribe({
              next: () => {
                console.log(`Successfully removed ${event.projectName} from workspace`);
                this.toastService.showSuccess(`"${event.projectName}" has been removed from the workspace.`, {
                  header: 'Project Removed'
                });
                // No need to refresh - signals will update reactively
              },
              error: (error) => {
                console.error('Error removing project from workspace:', error);
                this.toastService.showError(`Failed to remove "${event.projectName}" from workspace. Please try again.`, {
                  header: 'Remove Failed'
                });
              }
            });
        }
      });
  }

  protected retryInitialLoad(): void {
    this.keyProjectsService.retryInitialLoad();
  }

  /**
   * Rebuild all workspaces (DEV/LOCAL only)
   * Deletes all existing workspaces and recreates the 3 default workspaces
   */
  protected rebuildAllWorkspaces(): void {
    this.modalService
      .confirmDanger(
        'Rebuild All Workspaces',
        'This will delete all existing workspaces and recreate the 3 default workspaces (Most Active Projects, All Projects with Activities, Key Projects). This action cannot be undone. Continue?'
      )
      .subscribe(confirmed => {
        if (confirmed) {
          this.keyProjectsService.rebuildAllWorkspaces().subscribe({
            error: () => {
              // Error toast already shown by service
            }
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.clearDatadogWorkspaceContext();
    this.featureTracking.clearFeatureContext();
  }

  private clearDatadogWorkspaceContext(): void {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.removeGlobalContextProperty('keyProject');
    }
  }
}
